import React from 'react';
import {
  InsertDriveFile as DefaultFileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocumentIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
  Archive as ArchiveIcon,
  Code as CodeIcon,
  TableChart as SpreadsheetIcon
} from '@mui/icons-material';
import { Box } from '@mui/material';

// File type mapping based on file extensions
const FILE_TYPE_CONFIG = {
  // Images
  jpg: { icon: ImageIcon, color: '#4CAF50', category: 'image' },
  jpeg: { icon: ImageIcon, color: '#4CAF50', category: 'image' },
  png: { icon: ImageIcon, color: '#4CAF50', category: 'image' },
  gif: { icon: ImageIcon, color: '#4CAF50', category: 'image' },
  webp: { icon: ImageIcon, color: '#4CAF50', category: 'image' },
  svg: { icon: ImageIcon, color: '#4CAF50', category: 'image' },
  
  // Documents
  pdf: { icon: PdfIcon, color: '#F44336', category: 'document' },
  doc: { icon: DocumentIcon, color: '#2196F3', category: 'document' },
  docx: { icon: DocumentIcon, color: '#2196F3', category: 'document' },
  txt: { icon: DocumentIcon, color: '#757575', category: 'document' },
  rtf: { icon: DocumentIcon, color: '#757575', category: 'document' },
  
  // Spreadsheets
  xls: { icon: SpreadsheetIcon, color: '#4CAF50', category: 'spreadsheet' },
  xlsx: { icon: SpreadsheetIcon, color: '#4CAF50', category: 'spreadsheet' },
  csv: { icon: SpreadsheetIcon, color: '#4CAF50', category: 'spreadsheet' },
  
  // Videos
  mp4: { icon: VideoIcon, color: '#FF9800', category: 'video' },
  avi: { icon: VideoIcon, color: '#FF9800', category: 'video' },
  mov: { icon: VideoIcon, color: '#FF9800', category: 'video' },
  wmv: { icon: VideoIcon, color: '#FF9800', category: 'video' },
  webm: { icon: VideoIcon, color: '#FF9800', category: 'video' },
  
  // Audio
  mp3: { icon: AudioIcon, color: '#9C27B0', category: 'audio' },
  wav: { icon: AudioIcon, color: '#9C27B0', category: 'audio' },
  flac: { icon: AudioIcon, color: '#9C27B0', category: 'audio' },
  aac: { icon: AudioIcon, color: '#9C27B0', category: 'audio' },
  
  // Archives
  zip: { icon: ArchiveIcon, color: '#795548', category: 'archive' },
  rar: { icon: ArchiveIcon, color: '#795548', category: 'archive' },
  '7z': { icon: ArchiveIcon, color: '#795548', category: 'archive' },
  tar: { icon: ArchiveIcon, color: '#795548', category: 'archive' },
  gz: { icon: ArchiveIcon, color: '#795548', category: 'archive' },
  
  // Code
  js: { icon: CodeIcon, color: '#FFC107', category: 'code' },
  html: { icon: CodeIcon, color: '#FF5722', category: 'code' },
  css: { icon: CodeIcon, color: '#2196F3', category: 'code' },
  json: { icon: CodeIcon, color: '#607D8B', category: 'code' },
  xml: { icon: CodeIcon, color: '#607D8B', category: 'code' },
  py: { icon: CodeIcon, color: '#3776ab', category: 'code' },
  java: { icon: CodeIcon, color: '#f89820', category: 'code' },
  php: { icon: CodeIcon, color: '#777bb4', category: 'code' }
};

const FileTypeIcon = ({ fileName, size = 'medium', showBackground = false }) => {
  // Extract file extension
  const getFileExtension = (filename) => {
    if (!filename) return '';
    const extension = filename.split('.').pop()?.toLowerCase();
    return extension || '';
  };

  const extension = getFileExtension(fileName);
  const fileConfig = FILE_TYPE_CONFIG[extension] || {
    icon: DefaultFileIcon,
    color: '#757575',
    category: 'unknown'
  };

  const IconComponent = fileConfig.icon;

  // Size mapping
  const sizeMap = {
    small: 16,
    medium: 24,
    large: 32,
    xlarge: 48
  };

  const iconSize = sizeMap[size] || sizeMap.medium;

  if (showBackground) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: iconSize + 8,
          height: iconSize + 8,
          borderRadius: 1,
          bgcolor: `${fileConfig.color}20`, // 20% opacity
          border: `1px solid ${fileConfig.color}40` // 40% opacity
        }}
      >
        <IconComponent
          sx={{
            fontSize: iconSize,
            color: fileConfig.color
          }}
        />
      </Box>
    );
  }

  return (
    <IconComponent
      sx={{
        fontSize: iconSize,
        color: fileConfig.color
      }}
    />
  );
};

// Helper function to get file category
export const getFileCategory = (fileName) => {
  const extension = fileName ? fileName.split('.').pop()?.toLowerCase() : '';
  return FILE_TYPE_CONFIG[extension]?.category || 'unknown';
};

// Helper function to check if file is previewable
export const isPreviewable = (fileName) => {
  const category = getFileCategory(fileName);
  return ['image', 'document'].includes(category);
};

// Helper function to get all available categories
export const getFileCategories = () => {
  const categories = new Set();
  Object.values(FILE_TYPE_CONFIG).forEach(config => {
    categories.add(config.category);
  });
  return Array.from(categories);
};

export default FileTypeIcon;